import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { RefreshCw, Calendar, User, Home, DollarSign, ChevronRight, Plus } from 'lucide-react';
import { bookingAPI } from '../../lib/api';
import toast from 'react-hot-toast';

const PIPELINE_STAGES = [
  { key: 'pending',     label: 'Pending',     color: '#F59E0B', bg: '#FEF3C7' },
  { key: 'confirmed',   label: 'Confirmed',   color: '#3B82F6', bg: '#EFF6FF' },
  { key: 'checked_in',  label: 'Checked In',  color: '#8B5CF6', bg: '#EDE9FE' },
  { key: 'checked_out', label: 'Checked Out', color: '#10B981', bg: '#ECFDF5' },
  { key: 'cancelled',   label: 'Cancelled',   color: '#EF4444', bg: '#FEF2F2' },
];

const PAYMENT_BADGE = {
  paid:     { label: 'Paid',     cls: 'badge-green' },
  partial:  { label: 'Partial',  cls: 'badge-yellow' },
  pending:  { label: 'Unpaid',   cls: 'badge-gray' },
  refunded: { label: 'Refunded', cls: 'badge-red' },
  failed:   { label: 'Failed',   cls: 'badge-red' },
};

function safeFormat(date, fmt) {
  try { return format(new Date(date), fmt); } catch { return '—'; }
}

function BookingCard({ booking, onStatusChange }) {
  const navigate = useNavigate();
  const guest = booking.guest;
  const guestName = guest ? `${guest.firstName || ''} ${guest.lastName || ''}`.trim() : '—';
  const payBadge = PAYMENT_BADGE[booking.payment?.status] || PAYMENT_BADGE.pending;

  return (
    <div
      className="card"
      style={{ padding: 14, cursor: 'pointer', marginBottom: 10, transition: 'box-shadow 0.15s' }}
      onClick={() => navigate(`/bookings/${booking._id}`)}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = ''}
    >
      {/* Booking Number */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: '#3B82F6' }}>
          #{booking.bookingNumber || booking._id?.slice(-6).toUpperCase()}
        </span>
        <span className={`badge ${payBadge.cls}`} style={{ fontSize: 10 }}>{payBadge.label}</span>
      </div>

      {/* Property */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <Home size={13} color="#9CA3AF" />
        <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }} title={booking.property?.title}>
          {booking.property?.title?.length > 22 ? booking.property.title.slice(0, 22) + '…' : booking.property?.title || '—'}
        </span>
      </div>

      {/* Guest */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <User size={13} color="#9CA3AF" />
        <span style={{ fontSize: 13, color: '#374151' }}>{guestName}</span>
      </div>

      {/* Dates */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <Calendar size={13} color="#9CA3AF" />
        <span style={{ fontSize: 12, color: '#6B7280' }}>
          {safeFormat(booking.checkIn, 'MMM d')} → {safeFormat(booking.checkOut, 'MMM d, yyyy')}
          <span style={{ marginLeft: 4, color: '#9CA3AF' }}>({booking.nights}n)</span>
        </span>
      </div>

      {/* Amount */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <DollarSign size={13} color="#10B981" />
          <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
            ${booking.pricing?.total?.toLocaleString() || '—'}
          </span>
        </div>
        <div style={{ fontSize: 11, color: '#9CA3AF' }}>
          {booking.guests?.adults || 1} guest{(booking.guests?.adults || 1) > 1 ? 's' : ''}
        </div>
      </div>

      {/* OTA badge if from channel manager */}
      {booking.otaData?.source && (
        <div style={{ marginTop: 8, fontSize: 11, padding: '2px 8px', background: '#F3F4F6', color: '#374151', borderRadius: 999, display: 'inline-block' }}>
          via {booking.otaData.source.replace('_', '.')}
        </div>
      )}
    </div>
  );
}

export default function BookingPipelinePage() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await bookingAPI.getAll({ limit: 200 });
      setBookings(res.data.data || []);
    } catch {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const handleDragStart = (e, booking) => {
    setDragging(booking);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, stageKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOver(stageKey);
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    setDragOver(null);
    if (!dragging || dragging.status === targetStage) { setDragging(null); return; }

    const originalStatus = dragging.status;
    // Optimistic update
    setBookings(prev => prev.map(b => b._id === dragging._id ? { ...b, status: targetStage } : b));
    try {
      await bookingAPI.updateStatus(dragging._id, { status: targetStage });
      toast.success(`Booking moved to ${PIPELINE_STAGES.find(s => s.key === targetStage)?.label}`);
    } catch (err) {
      // Revert
      setBookings(prev => prev.map(b => b._id === dragging._id ? { ...b, status: originalStatus } : b));
      toast.error(err.response?.data?.message || 'Failed to update status');
    }
    setDragging(null);
  };

  const filteredBookings = bookings.filter(b => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const guestName = `${b.guest?.firstName || ''} ${b.guest?.lastName || ''}`.toLowerCase();
    return (
      guestName.includes(q) ||
      b.property?.title?.toLowerCase().includes(q) ||
      b.bookingNumber?.toLowerCase().includes(q)
    );
  });

  const getStageBookings = (stageKey) => filteredBookings.filter(b => b.status === stageKey);

  const totalRevenue = bookings
    .filter(b => b.status !== 'cancelled')
    .reduce((sum, b) => sum + (b.pricing?.total || 0), 0);

  return (
    <div style={{ height: '100%' }}>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Booking Pipeline</h1>
          <p className="page-subtitle">Drag & drop bookings between stages to update status</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input
            className="form-input"
            style={{ width: 220 }}
            placeholder="Search bookings..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <button className="btn btn-secondary" onClick={fetchBookings} disabled={loading}>
            <RefreshCw size={16} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/bookings/new')}>
            <Plus size={16} /> New Booking
          </button>
        </div>
      </div>

      {/* Summary bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {PIPELINE_STAGES.map(stage => {
          const count = getStageBookings(stage.key).length;
          const revenue = getStageBookings(stage.key).reduce((s, b) => s + (b.pricing?.total || 0), 0);
          return (
            <div key={stage.key} style={{ padding: '8px 16px', background: stage.bg, borderRadius: 8, borderLeft: `3px solid ${stage.color}` }}>
              <div style={{ fontSize: 11, color: stage.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{stage.label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>{count}</div>
              {revenue > 0 && <div style={{ fontSize: 11, color: '#6B7280' }}>${revenue.toLocaleString()}</div>}
            </div>
          );
        })}
        <div style={{ padding: '8px 16px', background: '#F0FDF4', borderRadius: 8, borderLeft: '3px solid #10B981', marginLeft: 'auto' }}>
          <div style={{ fontSize: 11, color: '#10B981', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Revenue</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>${totalRevenue.toLocaleString()}</div>
          <div style={{ fontSize: 11, color: '#6B7280' }}>{bookings.filter(b => b.status !== 'cancelled').length} active bookings</div>
        </div>
      </div>

      {/* Kanban Board */}
      {loading ? (
        <div className="loading-center"><div className="spinner spinner-lg" /></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${PIPELINE_STAGES.length}, 1fr)`, gap: 12, alignItems: 'start' }}>
          {PIPELINE_STAGES.map(stage => {
            const stageBookings = getStageBookings(stage.key);
            const isOver = dragOver === stage.key;
            return (
              <div
                key={stage.key}
                onDragOver={e => handleDragOver(e, stage.key)}
                onDragLeave={() => setDragOver(null)}
                onDrop={e => handleDrop(e, stage.key)}
                style={{
                  minHeight: 200,
                  background: isOver ? stage.bg : '#F9FAFB',
                  borderRadius: 12,
                  border: `2px dashed ${isOver ? stage.color : 'transparent'}`,
                  transition: 'all 0.15s',
                  padding: '0 4px 4px'
                }}
              >
                {/* Column Header */}
                <div style={{
                  position: 'sticky', top: 0,
                  padding: '12px 10px 10px',
                  borderRadius: '12px 12px 0 0',
                  background: stage.bg,
                  marginBottom: 10,
                  borderBottom: `2px solid ${stage.color}30`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: stage.color }} />
                      <span style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>{stage.label}</span>
                    </div>
                    <span style={{
                      fontSize: 12, fontWeight: 700, padding: '2px 8px',
                      background: stage.color, color: '#fff', borderRadius: 999
                    }}>{stageBookings.length}</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4, paddingLeft: 18 }}>
                    ${stageBookings.reduce((s, b) => s + (b.pricing?.total || 0), 0).toLocaleString()}
                  </div>
                </div>

                {/* Cards */}
                <div style={{ padding: '0 6px' }}>
                  {stageBookings.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '24px 12px', color: '#D1D5DB', fontSize: 13 }}>
                      Drop here
                    </div>
                  ) : stageBookings.map(booking => (
                    <div
                      key={booking._id}
                      draggable
                      onDragStart={e => handleDragStart(e, booking)}
                      onDragEnd={() => { setDragging(null); setDragOver(null); }}
                      style={{ opacity: dragging?._id === booking._id ? 0.5 : 1, cursor: 'grab' }}
                    >
                      <BookingCard booking={booking} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
